<template>
  <div
    :class="[
      'd-flex','flex-column','min-vh-100','page',
      { 'sidenav-open': isMobile && $store.state.showSidenav }
    ]"
  >
    <!-- Side Nav -->
    <sidenav
      :custom_class="$store.state.mcolor"
      class="bg-white fixed-start"
      v-if="$store.state.showSidenav && (!isFullBleed || isMap)"
    />

<div
  v-if="isMobile && $store.state.showSidenav && (!isFullBleed || isMap)"
  class="sidenav-backdrop"
  @click="closeSidenav"
/>

    <!-- Top Navbar -->
    <navbar
      :class="[navClasses]"
      :textWhite="textWhite"
      :minNav="navbarMinimize"
      @open-help="startGuide"
      v-if="$store.state.showNavbar && (!isFullBleed || isMap)"
    />

    <div class="topbar-offset"
      v-if="$store.state.showNavbar && !isFullBleed"
    >
      <div class="main-inner" :style="topbarInnerStyle">
        <PageHeader
          :title="currentPageTitle"
          :overline="currentMenuTitle"
          :subtitle="routeSubtitle"
          icon="ti ti-layout-grid"
        />
      </div>
    </div>

    <!-- Main Content (사이드바 오프셋 + 가운데 정렬) -->
    <main class="main-content position-relative flex-grow-1 border-radius-lg pb-6">
      <div class="main-inner" :style="mainInnerStyle">
        <RouterView v-slot="{ Component, route }">
          <template v-if="!route.meta.noTransition">
            <Transition name="fade-blur" mode="out-in" appear>
              <component :is="Component" :key="getRouteKey(route)"/>
            </Transition>
          </template>
          <template v-else>
            <component :is="Component" :key="getRouteKey(route)"/>
          </template>
        </RouterView>
      </div>
    </main>
    <app-footer v-show="$store.state.showFooter && !isFullBleed" class="topbar-offset" />

  </div>

  <GlobalLoading />
</template>

<script>
import GlobalLoading from "@/components/GlobalLoading.vue";
import Sidenav from "./examples/Sidenav";
import Navbar from "@/examples/Navbars/Navbar.vue";
import AppFooter from "@/examples/Footer.vue";
import PageHeader from "@/examples/PageHeader.vue";
import { mapMutations } from "vuex";
import { bus, RUN_GUIDE, GUIDE_REGISTER, GUIDE_UNREGISTER } from '@/bus/bus.js';

export default {
  name: "App",
  components: {
    Sidenav,
    Navbar,
    AppFooter,
    PageHeader,
    GlobalLoading,
  },
  provide() {
    const self = this;
    return {
      showLoading(text = "로딩중...") {
        self.loadingText = text;
        self.loadingVisible = true;
      },
      hideLoading() {
        self.loadingVisible = false;
        self.loadingText = "";
      },
      getLoadingText() {
        return self.loadingText;
      },
      getLoadingVisible() {
        return self.loadingVisible;
      },
    };
  },
  computed: {
    isFullBleed() {
      return this.$route.meta?.fullBleed === true;
    },
    isMap() {
      return this.$route.meta?.isMap === true;
    },
    contentMaxWidth() {
      const n = Number(this.$route.meta?.maxWidth);
      return Number.isFinite(n) && n > 0 ? n : 1500;
    },
    mainInnerStyle() {
      return this.isFullBleed
        ? { width: "100%", marginInline: "0" }
        : { width: "100%", maxWidth: this.contentMaxWidth + "px", marginInline: "auto" };
    },
    topbarInnerStyle() {
      return this.mainInnerStyle;
    },
    navClasses() {
      return {
        "position-sticky blur shadow-blur left-auto top-1 z-index-sticky":
          this.$store.state.isNavFixed,
        "position-absolute px-4 mx-0 w-100 z-index-2":
          this.$store.state.isAbsolute,
      };
    },
    currentMenuTitle() {
      const pageTitle = this.$route.meta.pageTitle || "";
      return pageTitle.split(" - ")[0] || "";
    },
    currentPageTitle() {
      const pageTitle = this.$route.meta.pageTitle || "";
      return pageTitle.split(" - ")[1] || "";
    },
    routeSubtitle() {
      return this.$route.meta.subtitle || "";
    },
    textWhite() {
      return this.$store.state.isAbsolute ? "text-white opacity-8" : "";
    },
    isMobile() { return this.vw < 1200; },
  },
  watch: {
    '$store.state.showSidenav'(val) {
      if (this.isMobile && val) document.body.classList.add('no-scroll');
      else document.body.classList.remove('no-scroll');
    }
  },
  data() {
    return {
      loadingVisible: false,
      loadingText: "",
      vw: window.innerWidth,
      guideCount: 0,
    };
  },
  created() {
    const token = localStorage.getItem("jwtToken");
    const userInfo = localStorage.getItem("userInfo");
    const mfInfo = localStorage.getItem("mfInfo");
    if (token && userInfo) {
      this.$store.commit("UserStore/setJwtToken", token);
      this.$store.commit("UserStore/setUserInfo", JSON.parse(userInfo));
      if(mfInfo) this.$store.commit("UserStore/setMfInfo", JSON.parse(mfInfo));
    }
    bus.on(GUIDE_REGISTER, () => { this.guideCount++; });
    bus.on(GUIDE_UNREGISTER, () => { this.guideCount = Math.max(0, this.guideCount - 1); });
  },
  beforeUnmount() {
    this.$store.state.isTransparent = "bg-white";
    window.removeEventListener('resize', this._onResize);
    this._unreg && this._unreg();
    document.body.classList.remove('no-scroll');
    bus.off(GUIDE_REGISTER);
    bus.off(GUIDE_UNREGISTER);
  },
  mounted() {
    if (this.isMobile) this.$store.commit('setShowSidenav', false);
    this._onResize = () => { this.vw = window.innerWidth; };
    window.addEventListener('resize', this._onResize);

    // 라우트 전환마다 자동 가이드 체크
    this._unreg = this.$router.afterEach(() => {
      if (this.isMobile) this.$store.commit('setShowSidenav', false);
      this._maybeAutoStartGuideFromQuery();
    });
  },
  methods: {
    ...mapMutations(["toggleConfigurator", "navbarMinimize"]),
    getRouteKey(route) {
      const keepParent = route.matched.some(r => r.meta && r.meta.keepParentOnChildRoute);
      return keepParent ? route.matched[0]?.path : route.fullPath;
    },
    closeSidenav() {
      this.$store.commit('setShowSidenav', false);
    },
    // guideCount>0 (현재 페이지가 가이드를 등록) 될 때까지 대기
    async _waitGuideReady(timeout = 1500) {
      const start = performance.now();
      while (performance.now() - start < timeout) {
        // 페이지 컴포넌트가 트랜지션으로 교체될 시간을 조금 준다
        await this.$nextTick();
        await new Promise(r => setTimeout(r, 50));
        if (this.guideCount > 0) return true;
      }
      return false; // 준비 안 됨
    },

    async _maybeAutoStartGuideFromQuery() {
      // ?tour=true 인지 확인
      if (this.$route.query?.tour !== 'true') return;

      // 현재 페이지가 가이드 등록(GUIDE_REGISTER)할 때까지 잠깐 대기
      const ready = await this._waitGuideReady(2800);

      if (ready) {
        // 실행!
        setTimeout(() => {
          this.startGuide();
        }, 800);
      }

      // 🔸 1회성으로 쓰고 쿼리 지워 재진입/새로고침시 재발동 방지
      const { query, name, params } = this.$route;
      if (query.tour) {
        const url = new URL(window.location.href);
        url.searchParams.delete('tour');
        window.history.replaceState({}, '', url.toString());
      }
    },

    // 기존 코드
    startGuide() {
      if (this.guideCount > 0) {
        const { view } = this.$route.query || {};
        bus.emit(RUN_GUIDE, view);
      } else {
        this.toastWarning?.('이 페이지에는 가이드가 없습니다.');
      }
    },
  },
};
</script>

<style>
html, body {
  height: 100%;
  margin: 0;
}

/* 모바일 주소창 이슈 대비 */
.page {
  min-height: 100svh;
  min-height: 100dvh;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.page-body {
  flex: 1 0 auto;
}

/* 라우트 트랜지션 */
.fade-blur-enter-active,
.fade-blur-leave-active {
  transition: opacity .28s ease, filter .28s ease;
}
.fade-blur-enter-from,
.fade-blur-leave-to {
  opacity: 0;
  filter: blur(4px);
}
.fade-blur-enter-to,
.fade-blur-leave-from {
  opacity: 1;
  filter: blur(0);
}

/* =============== 레이아웃 핵심 =============== */
:root {
  /* 기본: 모바일/태블릿에선 사이드바 오버레이 → 0 */
  --aside-offset: 0px;
}

/* 상단 crumbs 컨테이너: 사이드바 폭만큼 밀기 */
.topbar-offset {
  margin-left: var(--aside-offset);
  padding: .25rem 2rem 0;
}

/* 메인: 사이드바 폭만큼 밀기 + 좌우 패딩 */
.main-content {
  margin-left: var(--aside-offset);
  padding: 0 2rem 2rem;
}

@media (max-width: 1199.98px) {
  .main-content {
    padding: 0 !important;   /* 좌우 패딩 제거 */
  }
}

/* 내부 컨텐츠: 최대 1500px, 가운데 정렬 (반응형) */
.main-inner {
  width: 100%;
}

/* ------- PageHeader(= .topbar-offset)도 사이드바 만큼 밀어주기 ------- */
@media (min-width: 1200px) {
  /* LTR: 사이드바 펼침 */
  .sidenav.fixed-start ~ .topbar-offset {
    margin-left: 17.125rem; /* Soft UI 기본 open width */
  }
  /* LTR: 사이드바 미니(접힘) */
  .g-sidenav-hidden .sidenav.fixed-start ~ .topbar-offset {
    margin-left: 6rem; /* Soft UI 기본 mini width */
  }
}

/* 모바일/태블릿: 오버레이라서 오프셋 제거 */
@media (max-width: 1199.98px) {
  .topbar-offset {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
}

/* 바깥영역 클릭 감지용 오버레이 */
.sidenav-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.35);
  backdrop-filter: blur(1px);
  z-index: 8;        /* 콘텐츠 위, 사이드네비 아래 */
}

/* 사이드네비가 백드롭보다 위로 오도록 */
.sidenav.fixed-start {
  z-index: 9;
}

/* 모바일에서 네비 열릴 때 본문 스크롤 잠금(선택) */
.no-scroll {
  overflow: hidden;
}

@media (max-width: 1199.98px) {
  .sidenav-open .sidenav {
    transform: translateX(0) !important;  /* ✅ 핵심 */
  }
}

</style>
